import { phaseColor } from '@/pages/MonthlyAccounting';
import { composeTaskTitle, splitTaskTitle, type TitleOptions } from '@/tasks/task-title';
import type { Task } from '@/tasks/task';
import { businessColor } from './BusinessLabel';
import { PillSelect } from './PillSelect';

/**
 * ProgressTitleCell の props。
 */
export interface ProgressTitleCellProps {
  /** 行のタスク */
  task: Task;
  /** そのタスクの種別の区分の選択肢。無ければ選ばせない */
  options?: TitleOptions;
  /** 大分類のピルも出すか。大分類でまとめているときは見出しに出ているので出さない */
  showCategory: boolean;
  /** 区分を選び直したときに呼ばれる。内容を組み立て直したタスクを渡す */
  onChange: (task: Task) => void;
  /** 作業の名前を押したときに呼ばれる（詳細を開く） */
  onOpen: () => void;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 進捗の行の内容を、詳細画面と同じパステルの丸いプルダウン（事業・中分類、必要なら大分類）と作業の名前に分けて出す。区分はその場で選び直せ、名前を押すと詳細を開く
 * @graph-connects splitTaskTitle [calls] 内容を区分に分ける
 * @graph-connects composeTaskTitle [calls] 選び直した区分から内容を組み立てる
 * @graph-connects PillSelect [calls] 区分を丸いプルダウンで選ばせる
 */
export function ProgressTitleCell({
  task,
  options,
  showCategory,
  onChange,
  onOpen,
}: ProgressTitleCellProps) {
  const parts = splitTaskTitle(task.title);
  const name = (
    <button type="button" onClick={onOpen} className="cursor-pointer text-left hover:underline">
      {parts === null ? task.title : parts.name}
    </button>
  );
  if (parts === null || options === undefined) {
    return name;
  }
  const change = (patch: Partial<typeof parts>) => {
    const next = { ...parts, ...patch };
    // 大分類が変わったら、その大分類に無い中分類は外す
    if (
      patch.category !== undefined &&
      !(options.middleByCategory[next.category] ?? []).includes(next.middleCategory)
    ) {
      next.middleCategory = '';
    }
    onChange({ ...task, title: composeTaskTitle(next) });
  };
  const withCurrent = (values: readonly string[], current: string) =>
    [...new Set([...values, current].filter((value) => value !== ''))].sort((a, b) =>
      a.localeCompare(b, 'ja'),
    );
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span className="flex flex-wrap items-center gap-1 text-[11px] font-semibold">
        {showCategory ? (
          <PillSelect
            label={`${parts.name}の大分類`}
            value={parts.category}
            emptyLabel="大分類なし"
            allowEmpty={false}
            options={withCurrent(options.categories, parts.category)}
            color={phaseColor(parts.category).color}
            onChange={(category) => {
              change({ category });
            }}
          />
        ) : null}
        <PillSelect
          label={`${parts.name}の事業`}
          value={parts.business}
          emptyLabel="事業なし"
          allowEmpty={false}
          options={withCurrent(options.businesses, parts.business)}
          color={businessColor(parts.business).color}
          onChange={(business) => {
            change({ business });
          }}
        />
        <PillSelect
          label={`${parts.name}の中分類`}
          value={parts.middleCategory}
          emptyLabel="中分類なし"
          options={withCurrent(
            options.middleByCategory[parts.category] ?? [],
            parts.middleCategory,
          )}
          color={parts.middleCategory === '' ? undefined : 'var(--color-pill-middle)'}
          onChange={(middleCategory) => {
            change({ middleCategory });
          }}
        />
      </span>
      {name}
    </span>
  );
}
